import { createListenerMiddleware } from '@reduxjs/toolkit';
import {
  clearCart,
  clearAppliedECoupon,
  setAppliedECoupon,
  updateCartItems,
  updateLoggedUserEmailInput,
} from '../../../../../../libs/shared-utils/src/lib/stateManager/slices/myAreaCartSlice';
import { RootState } from '../../../../../../libs/shared-utils/src/lib/stateManager/store';

/*
 * WHY A LISTENER MIDDLEWARE?
 * In Redux, reducers and their actions must be pure functions: they should not have side effects like accessing
 * sessionStorage or making API calls. This ensures state updates are predictable and testable.
 * Side effects (like saving to sessionStorage) should be handled in middleware,
 * not inside reducers or their actions.
 * */

export const cartListener = createListenerMiddleware();

/**
 * Sets up a listener middleware for cart updates.
 *
 * Listens for actions matching `updateCartItems`. When such an action is dispatched,
 * retrieves the current cart items from the Redux state (`myAreaCart.items`),
 * serializes them to JSON, and saves them to `sessionStorage` under the key `cart`.
 * This persists the cart state for the browser session.
 */
cartListener.startListening({
  matcher: (action) => updateCartItems.match(action),
  effect: (_, listenerApi) => {
    const cart = (listenerApi.getState() as RootState).myAreaCart.items;
    sessionStorage.setItem('cart', JSON.stringify(cart));
    const cartUserEmail = (listenerApi.getState() as RootState).myAreaCart.loggedUserEmailInput;
    if (cartUserEmail) {
      sessionStorage.setItem('cartUserEmail', cartUserEmail);
    }
  },
});

cartListener.startListening({
  matcher: (action) => updateLoggedUserEmailInput.match(action),
  effect: (_, listenerApi) => {
    const cartUserEmail = (listenerApi.getState() as RootState).myAreaCart.loggedUserEmailInput;
    sessionStorage.setItem('cartUserEmail', cartUserEmail);
  },
});

cartListener.startListening({
  matcher: (action) => setAppliedECoupon.match(action),
  effect: (_, listenerApi) => {
    const appliedECoupon = (listenerApi.getState() as RootState).myAreaCart.appliedECoupon;
    if (appliedECoupon) {
      sessionStorage.setItem('cartECoupon', appliedECoupon);
    }
  },
});

cartListener.startListening({
  matcher: (action) => clearAppliedECoupon.match(action),
  effect: () => {
    sessionStorage.removeItem('cartECoupon');
  },
});

cartListener.startListening({
  matcher: (action) => clearCart.match(action),
  effect: () => {
    sessionStorage.removeItem('cart');
    sessionStorage.removeItem('cartUserEmail');
    sessionStorage.removeItem('cartECoupon');
  },
});
